// The scatter props (D103, densities D105): what stands inside each terrain's tray, how
// big, how much room it needs, and how many. Data, not code — a tile that reads too bare
// or too crowded is a number here rather than a change in the scene graph.
//
// Sizes are TARGET HEIGHTS in world units, and every one is deliberately below the
// buildings (BUILDING_RADIUS 0.32 half-width): the board-art pivot began because scenery
// competed with pieces, so scenery stays smaller than the thing a player reads fastest.
//
// `spacing` is the prop's footprint RADIUS, and it does two jobs: two props clash when
// they are closer than the sum of their spacings, and a prop must stand at least its own
// spacing clear of the number disc. That is what lets a wheat field be dense and a stand
// of trees be sparse under one rule — small things pack, big things do not.

/** Where the optimised prop exports live, under MODEL_BASE. */
export const PROP_DIR = 'props';

export interface PropSpec {
  /** Target height in world units — a hex side is 1.0. */
  height: number;
  /** Footprint radius, for spacing and disc clearance. */
  spacing: number;
  /**
   * How far to sink the model into the ground, world units.
   *
   * The reference images were painted standing on little earth pucks and Meshy modelled
   * those pucks faithfully. Burying the base hides the puck's rim and beds the prop into
   * the terrain it stands on; props with no puck take nothing.
   */
  sink?: number;
  /** Relative frequency inside its terrain's pool. Default 1. */
  weight?: number;
  /** Hard cap per tile — one cart in a field, one ladder in a mine. */
  max?: number;
  /** Model path under MODEL_BASE. Defaults to `props/<key>`. */
  path?: string;
  /** Lay the prop on its side instead of standing it up (the mine's ladder). */
  lay?: boolean;
  /** Seek the steepest spot on the tile — a ladder belongs against a mine wall. */
  prefersSlope?: boolean;
  /**
   * This prop is RIGGED and animated, so it cannot be instanced (D109).
   *
   * Skinning is per-skeleton, and an InstancedMesh shares one — so the sheep are built as
   * individual clones with their own mixers instead. There are a dozen or so on a board,
   * which is nothing; the instancing was never what made them cheap.
   */
  rigged?: boolean;
  /**
   * D232/D235: this prop RATTLES when a moving pointer drags over it, and HOW.
   *
   * `sway` is how far its TIP travels in world units at full energy — travel rather than an
   * angle, so a tall prop does not swing further than a short one just for being tall (the
   * user's "adjust it for the tree size"). `curve` is the exponent on normalised height: 1
   * leans the whole thing like a stalk, 3 moves only the top third like a tree's crown.
   * Opt-in per kind; everything else stands perfectly still and costs nothing.
   */
  rattle?: { sway: number; curve: number };
  /**
   * This prop IS metal (D280): its clone gets metalness 1 at this roughness, so it catches the
   * stage's baked sky like the number chips do (D156). The loader zeroes every model's metalness
   * — rightly for rock, soil and thatch — so without this a gold nugget reads as an orange shrub.
   */
  metal?: { roughness: number };
  /**
   * Only a board with WATER loads this prop (D280) — it belongs to a terrain only the Open Sea
   * deals, so a classic board never fetches it. Same test as the sea's textures and ship.
   */
  sea?: boolean;
  /**
   * Multiply the model's own colour, e.g. to take borrowed art off pure white.
   *
   * The pasture's sheep are the shelf token, painted bright for a cream HUD; against a
   * green meadow they read as paper cut-outs, so they are darkened here rather than in
   * the shared art, which the shelf still uses.
   */
  tint?: string;
  /**
   * Brightness applied after `tint`, for art that needs LIFTING rather than damping.
   *
   * A tint can only ever multiply a hex value ≤ 1, so on its own it darkens — fine for the
   * sheep, useless for wheat that already reads washed out. Saturation does most of the work
   * (a "faded" colour is a desaturated one, D108), and this is the small lift on top.
   */
  gain?: number;
}

/** Every prop, by key — the key is also its filename under `props/` unless `path` says otherwise. */
export const PROPS: Readonly<Record<string, PropSpec>> = Object.freeze({
  // fields — a WHEAT FIELD: sprouts everywhere, one cart, one scarecrow
  // D167: the sheaves read pale and faded — the tint cuts blue hard for gold, the gain lifts it
  'wheat-sprout': { height: 0.19, spacing: 0.043, weight: 20, tint: '#ffd766', gain: 1.25, rattle: { sway: 0.05, curve: 1.5 } },
  scarecrow: { height: 0.32, spacing: 0.16, sink: 0.04, weight: 1, max: 1 },
  'hay-bale': { height: 0.17, spacing: 0.12, weight: 2, max: 3, tint: '#ffd766', gain: 1.25 },
  cart: { height: 0.2, spacing: 0.17, weight: 1, max: 1 },
  // pasture — real sheep grazing among grass and flowers
  grass: { height: 0.13, spacing: 0.08, sink: 0.03, weight: 6 },
  sheep: { height: 0.25, spacing: 0.15, weight: 16, max: 9, path: 'resources/sheep_walking', tint: '#cbbfae', rigged: true },
  flowers: { height: 0.14, spacing: 0.09, sink: 0.03, weight: 4 },
  trough: { height: 0.14, spacing: 0.14, weight: 1, max: 1 },
  // forest — a FOREST: many trees, smaller than before so they can crowd
  tree: { height: 0.28, spacing: 0.082, sink: 0.03, weight: 16, rattle: { sway: 0.028, curve: 3 } },
  'tree-round': { height: 0.26, spacing: 0.082, sink: 0.03, weight: 3, rattle: { sway: 0.028, curve: 3 } },
  mushrooms: { height: 0.12, spacing: 0.08, sink: 0.03, weight: 2, max: 3 },
  stump: { height: 0.15, spacing: 0.11, sink: 0.02, weight: 1, max: 2 },
  'log-pile': { height: 0.15, spacing: 0.13, weight: 1, max: 1 },
  // mountains — ROCKY: boulders and ore everywhere, a few peaks and crags
  boulder: { height: 0.18, spacing: 0.12, sink: 0.03, weight: 7 },
  'ore-rock': { height: 0.15, spacing: 0.1, weight: 4 },
  crag: { height: 0.28, spacing: 0.17, sink: 0.05, weight: 2, max: 3 },
  // hills — a WORKING MINE: stacks of brick, one barrow, one ladder down a steep face
  'brick-stack': { height: 0.15, spacing: 0.1, weight: 9 },
  wheelbarrow: { height: 0.18, spacing: 0.15, weight: 2, max: 1 },
  ladder: { height: 0.26, spacing: 0.15, weight: 3, max: 1, lay: true, prefersSlope: true },
  // desert
  cactus: { height: 0.28, spacing: 0.13, weight: 3, max: 1 },
  tumbleweed: { height: 0.16, spacing: 0.1, weight: 2 },
  // gold (D280, the Open Sea only) — a PLACER FIELD: nugget piles in the gravel, a pan or two,
  // one sluice box. The nuggets carry the read; the pan and the sluice say "worked", not "rich"
  'gold-nuggets': { height: 0.1, spacing: 0.09, weight: 6, sea: true, metal: { roughness: 0.35 } },
  'gold-pan': { height: 0.05, spacing: 0.09, weight: 2, max: 2, sea: true },
  sluice: { height: 0.2, spacing: 0.15, weight: 1, max: 1, sea: true },
});

/** Which props each terrain draws from. Keys are the engine's tile ids. */
export const TILE_PROPS: Readonly<Record<string, readonly string[]>> = Object.freeze({
  fields: ['wheat-sprout', 'scarecrow', 'hay-bale', 'cart'],
  pasture: ['grass', 'sheep', 'flowers', 'trough'],
  forest: ['tree', 'tree-round', 'mushrooms', 'stump', 'log-pile'],
  mountains: ['boulder', 'ore-rock', 'crag'],
  hills: ['brick-stack', 'wheelbarrow', 'ladder'],
  desert: ['cactus', 'tumbleweed'],
  gold: ['gold-nuggets', 'gold-pan', 'sluice'],
});

/**
 * How many props each terrain aims for per tile.
 *
 * TARGETS, not guarantees: the scatter drops any candidate that will not fit, so what a
 * tile actually carries falls out of the props' own footprints — a field of small sprouts
 * packs to its target, a stand of big trees settles for fewer. These numbers are what
 * make a wheat field read as a field and a forest as a forest rather than as a few
 * ornaments (D105).
 *
 * The DESERT carries no number disc, so it has no clearing to keep and fills right
 * across its middle — which is why it can hold as much as a field despite a small pool.
 */
export const TILE_PROP_COUNT: Readonly<Record<string, number>> = Object.freeze({
  fields: 68,
  pasture: 20,
  forest: 33,
  mountains: 12,
  hills: 12,
  desert: 16,
  // D318: the gorge — the pool and its banks take the middle of the tile, so the piles, a pan and
  // the sluice stand on the rock round it; twelve hid the painting and crowded onto the rim
  gold: 5,
});

/** Where a prop's model lives, relative to MODEL_BASE. */
export function propPath(key: string): string {
  return PROPS[key]?.path ?? `${PROP_DIR}/${key}`;
}

/** Every prop the board loads, for the loader — the sea's own only when the board has water (D280). */
export function allProps(sea = false): string[] {
  return Object.keys(PROPS).filter((key) => sea || PROPS[key]?.sea !== true);
}
